import { z } from "zod";
import { PERMISSION_KEYS, DEFAULT_STAFF_PERMISSIONS } from "./permissions.js";
export const slugSchema = z
    .string()
    .trim()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
export const pinSchema = z
    .string()
    .trim()
    .regex(/^\d{4,8}$/, "PIN must contain 4 to 8 digits.");
export const permissionMapSchema = z.object(Object.fromEntries(PERMISSION_KEYS.map((key) => [
    key,
    z.boolean().default(DEFAULT_STAFF_PERMISSIONS[key])
])));
export const getLoginOptionsInputSchema = z.object({
    slug: slugSchema
});
export const loginWithPinInputSchema = z.object({
    slug: slugSchema,
    membershipId: z.string().min(1).max(128),
    pin: pinSchema
});
export const getSessionBootstrapInputSchema = z.object({
    organizationId: z.string().min(1).max(128).optional()
});
export const changePinInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    currentPin: pinSchema.optional(),
    newPin: pinSchema
});
export const createStaffMemberInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    displayName: z.string().trim().min(1).max(120),
    loginName: z.string().trim().min(1).max(120).optional(),
    email: z
        .string()
        .trim()
        .email()
        .or(z.literal(""))
        .optional(),
    temporaryPin: pinSchema,
    role: z.enum([
        "staff",
        "seniorStaff",
        "admin",
        "superAdmin"
    ]),
    permissions: permissionMapSchema.partial().optional()
});
export const resetStaffPinInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    userId: z.string().min(1).max(128),
    temporaryPin: pinSchema
});
export const createSubmissionInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    categoryId: z.string().min(1).max(128),
    payload: z.record(z.string(), z.unknown()),
    notes: z.string().trim().max(500).default(""),
    clientRequestId: z.string().min(8).max(128),
    allowDuplicate: z.boolean().default(false),
    submissionIntent: z.enum(["standard", "repeat"]).default("standard"),
    sourceSubmissionId: z.string().min(1).max(128).optional()
});
export const listRecentSubmissionsInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    limit: z.number().int().min(1).max(100).default(30)
});
export const reverseSubmissionInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    submissionId: z.string().min(1).max(128),
    reason: z.string().trim().max(300).default("Staff undo")
});
export const editSubmissionInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    submissionId: z.string().min(1).max(128),
    payload: z.record(z.string(), z.unknown()),
    notes: z.string().trim().max(500).default(""),
    clientRequestId: z.string().min(8).max(128)
});
const colorSchema = z.string().trim().regex(/^#[0-9A-Fa-f]{6}$/);
export const getAdminPortalDataInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    submissionLimit: z.number().int().min(20).max(500).default(200)
});
export const updateStaffMemberInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    userId: z.string().min(1).max(128),
    displayName: z.string().trim().min(1).max(120),
    loginName: z.string().trim().min(1).max(120),
    email: z.string().trim().email().or(z.literal("")),
    role: z.enum(["staff", "seniorStaff", "admin", "superAdmin"]),
    permissions: permissionMapSchema,
    active: z.boolean(),
    loginVisible: z.boolean(),
    sortOrder: z.number().int().min(0).max(99999)
});
export const saveCabinInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    cabinId: z.string().min(1).max(128).optional(),
    name: z.string().trim().min(1).max(80),
    shortName: z.string().trim().min(1).max(20),
    number: z.number().int().min(0).max(999).nullable(),
    color: colorSchema,
    textColor: colorSchema,
    sortOrder: z.number().int().min(0).max(99999),
    activeGlobally: z.boolean(),
    archived: z.boolean()
});
export const saveWeekInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    weekId: z.string().min(1).max(128).optional(),
    name: z.string().trim().min(1).max(80),
    startDate: z.string().datetime(),
    endDate: z.string().datetime(),
    status: z.enum(["upcoming", "active", "closed", "archived"]),
    activeCabinIds: z.array(z.string().min(1).max(128)).max(200),
    enabledCategoryIds: z.array(z.string().min(1).max(128)).max(200),
    makeCurrent: z.boolean().default(false)
});
export const saveCategoryInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    categoryId: z.string().min(1).max(128).optional(),
    name: z.string().trim().min(1).max(100),
    slug: slugSchema,
    icon: z.string().trim().min(1).max(30),
    color: colorSchema,
    enabled: z.boolean(),
    archived: z.boolean(),
    sortOrder: z.number().int().min(0).max(99999),
    scoringMode: z.enum([
        "fixedMultiCabin",
        "singleCabinQuantity",
        "multipleCabinQuantity",
        "customPerCabin",
        "winners",
        "placement",
        "multiComponent",
        "activityReward",
        "manual"
    ]),
    formDefinition: z.record(z.string(), z.unknown()),
    scoringDefinition: z.record(z.string(), z.unknown()),
    approvalPolicy: z.object({
        mode: z.enum(["none", "permissionBased", "always"]),
        permissionKey: z.string().trim().max(80).optional()
    }),
    achievementDefinition: z.object({
        enabled: z.boolean(),
        name: z.string().trim().max(100),
        descriptionTemplate: z.string().trim().max(300)
    }),
    reportingDefinition: z.object({
        trackOccurrences: z.boolean().optional(),
        trackWins: z.boolean().optional(),
        winComponents: z.array(z.string().trim().min(1).max(60)).max(20).optional(),
        countLabel: z.string().trim().max(100).optional(),
        listLabel: z.string().trim().max(100).optional(),
        eventLabelMode: z.enum(["day", "optionalName", "nameRequired"]).optional()
    }).optional()
});
export const updateOrganizationSettingsInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    settings: z.object({
        currentWeekId: z.string().max(128),
        scoreVisibilityMode: z.enum(["disabled", "permissionBased"]),
        duplicateWindowSeconds: z.number().int().min(0).max(3600),
        submissionEditWindowMinutes: z.number().int().min(0).max(10080),
        quickSubmitDefault: z.boolean(),
        approvalsEnabled: z.boolean(),
        offlineSubmissionsEnabled: z.boolean()
    }),
    branding: z.object({
        campName: z.string().trim().min(1).max(120),
        shortName: z.string().trim().min(1).max(40),
        logoUrl: z.string().trim().max(500),
        primaryColor: colorSchema,
        accentColor: colorSchema,
        backgroundColor: colorSchema,
        reportCardTitle: z.string().trim().max(120),
        footerText: z.string().trim().max(300)
    })
});
export const reviewSubmissionInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    submissionId: z.string().min(1).max(128),
    decision: z.enum(["approve", "decline"]),
    reason: z.string().trim().max(500).default("")
});
export const bulkPointFilterSchema = z.object({
    weekId: z.string().max(128).optional(),
    categoryId: z.string().max(128).optional(),
    cabinId: z.string().max(128).optional(),
    currentPoints: z.number().optional()
});
export const previewBulkPointChangeInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    filter: bulkPointFilterSchema,
    transform: z.object({
        mode: z.enum(["replace", "add", "multiply"]),
        value: z.number().finite()
    })
});
export const applyBulkPointChangeInputSchema = previewBulkPointChangeInputSchema.extend({
    reason: z.string().trim().min(3).max(500)
});
export const rollbackBulkOperationInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    operationId: z.string().min(1).max(128),
    reason: z.string().trim().min(3).max(500)
});
export const adminCorrectSubmissionInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    submissionId: z.string().min(1).max(128),
    notes: z.string().trim().max(500),
    reason: z.string().trim().min(3).max(500),
    entries: z.array(z.object({
        cabinId: z.string().min(1).max(128),
        points: z.number().finite(),
        note: z.string().trim().max(300).default("")
    })).min(1).max(100)
});
export const reportSectionKeySchema = z.enum([
    "summary",
    "leaderboard",
    "categoryBreakdown",
    "dailyProgress",
    "eventWins",
    "onTime",
    "achievements",
    "topMoments",
    "detailedEntries",
    "comment"
]);
export const reportSectionConfigSchema = z.object({
    key: reportSectionKeySchema,
    enabled: z.boolean(),
    title: z.string().trim().min(1).max(100),
    order: z.number().int().min(0).max(1000),
    settings: z.record(z.string(), z.unknown()).optional()
});
export const getReportCenterDataInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    weekId: z.string().min(1).max(128)
});
export const saveReportTemplateInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    templateId: z.string().min(1).max(128).optional(),
    name: z.string().trim().min(1).max(100),
    description: z.string().trim().max(300),
    enabled: z.boolean(),
    isDefault: z.boolean(),
    layout: z.enum(["standard", "booklet"]),
    sections: z.array(reportSectionConfigSchema).min(1).max(20),
    style: z.object({
        logoTreatment: z.enum(["blend", "softPlate", "hidden"]),
        headerStyle: z.enum(["brandBand", "minimal"]),
        density: z.enum(["comfortable", "compact"]),
        showCabinColorBand: z.boolean()
    })
});
export const saveAchievementRuleInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    ruleId: z.string().min(1).max(128).optional(),
    name: z.string().trim().min(1).max(100),
    icon: z.string().trim().min(1).max(30),
    descriptionTemplate: z.string().trim().min(1).max(300),
    enabled: z.boolean(),
    metric: z.enum([
        "totalPoints",
        "overallRank",
        "categoryPoints",
        "categoryWins",
        "categoryOccurrences",
        "onTimeRate"
    ]),
    categoryId: z.string().min(1).max(128).nullable(),
    comparator: z.enum(["gte", "lte", "eq", "leader", "rankAtMost"]),
    threshold: z.number().finite(),
    showOnReport: z.boolean(),
    sortOrder: z.number().int().min(0).max(99999)
});
export const saveSubmissionReportMetadataInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    submissionId: z.string().min(1).max(128),
    reportEventLabel: z.string().trim().max(120),
    reportDayLabel: z.string().trim().max(80)
});
export const saveReportCommentInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    weekId: z.string().min(1).max(128),
    cabinId: z.string().min(1).max(128),
    comment: z.string().trim().max(2000)
});
export const createReportSnapshotInputSchema = z.object({
    organizationId: z.string().min(1).max(128),
    weekId: z.string().min(1).max(128),
    cabinId: z.string().min(1).max(128),
    templateId: z.string().min(1).max(128),
    reportData: z.record(z.string(), z.unknown())
});
